import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { sortIcon } from '../../../assets/images/icons';
import NewFilterDrawer from '../../../components/shared/FilterDrawer/NewFilterDrawer';

import SearchBar from '../../../components/SearchBar';
import UsersTable from './UsersTable';

import {
  useLazyGetAllCountriesQuery,
  useLazyGetCountryStatesByCountryIdQuery,
  useLazyGetStateCitiesByStateIdQuery,
} from '../../../services/CountriesAndStatesAndCities/CountriesAndStatesAndCities';
import { useLazyGetAllRolesQuery } from '../../../services/UserRoles/UserRolesService';
import { useLazyGetLocationsQuery } from '../../../services/Location/LocationService';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../constants/FilterConstants';
import SORT_CONSTANT, { LATEST_UPDATED } from '../../../constants/SortConstants';

import useStyles from './style';
import { useSearchFilterParam } from '../../../hooks';
import { resetActiveUsersFiltersState, setActiveUsersFilters } from '../../../redux-slice/usersFilters';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/pagination';

const ActiveTab = ({ onTotalUsers }) => {
  const classes = useStyles();
  const { DRAWER_TYPES, TITLE, FILTER_DATA } = FILTER_CONSTANTS;
  const dispatch = useDispatch();
  const { activeUsers } = useSelector((state) => state.usersFilter);

  const [searchText, setSearchText] = useState('');
  const [filterParams, setFilterParams] = useState(FILTER_DATA.USER.INITIAL_PARAMS);
  const [sort, setSort] = useState(LATEST_UPDATED);

  const [getLocations] = useLazyGetLocationsQuery();
  const [getAllRole] = useLazyGetAllRolesQuery();
  const [getAllCountries] = useLazyGetAllCountriesQuery();
  const [getStatesByCountryId] = useLazyGetCountryStatesByCountryIdQuery();
  const [getCitiesByStateId] = useLazyGetStateCitiesByStateIdQuery();

  const getAPI = (value) => {
    switch (value) {
      case FILTER_VALUES.ROLE:
        return getAllRole;
      case FILTER_VALUES.LOCATION:
        return getLocations;
      case FILTER_VALUES.COUNTRY:
        return getAllCountries;
      case FILTER_VALUES.STATE:
        return getStatesByCountryId;
      case FILTER_VALUES.CITY:
        return getCitiesByStateId;

      default:
        return [];
    }
  };

  const filterData = useMemo(
    () =>
      FILTER_DATA.USER.FILTER.map((item) => {
        const api = getAPI(item.value);

        return {
          ...item,
          api,
        };
      }),
    [],
  );

  useEffect(() => {
    const storedFilterParams = {
      [FILTER_VALUES.ROLE]: [],
      [FILTER_VALUES.LOCATION]: [],
      [FILTER_VALUES.COUNTRY]: null,
      [FILTER_VALUES.STATE]: null,
      [FILTER_VALUES.CITY]: [],
    };

    if (activeUsers[FILTER_VALUES.ROLE] && activeUsers[FILTER_VALUES.ROLE]?.length > 0) {
      storedFilterParams.role = activeUsers[FILTER_VALUES.ROLE];
    }
    if (activeUsers[FILTER_VALUES.LOCATION] && activeUsers[FILTER_VALUES.LOCATION]?.length > 0) {
      storedFilterParams.locationId = activeUsers[FILTER_VALUES.LOCATION];
    }
    if (activeUsers[FILTER_VALUES.COUNTRY] && Object.keys(activeUsers[FILTER_VALUES.COUNTRY]).length > 0) {
      storedFilterParams.countryId = activeUsers[FILTER_VALUES.COUNTRY];
    }
    if (activeUsers[FILTER_VALUES.STATE] && Object.keys(activeUsers[FILTER_VALUES.STATE]).length > 0) {
      storedFilterParams.stateId = activeUsers[FILTER_VALUES.STATE];
    }
    if (activeUsers[FILTER_VALUES.CITY] && activeUsers[FILTER_VALUES.CITY]?.length > 0) {
      storedFilterParams.cityId = activeUsers[FILTER_VALUES.CITY];
    }
    setSearchText(activeUsers.searchText);
    setSort(activeUsers.sort);
    setFilterParams(storedFilterParams);
  }, [activeUsers]);

  const searchParams = useSearchFilterParam({
    filterKey: FILTER_DATA.USER,
    filterParams,
    sort,
    searchText,
    extraParam: 'status=true',
  });

  const searchHandler = (params) => {
    setSearchText(params);
    dispatch(
      setActiveUsersFilters({
        ...activeUsers,
        searchText: params,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
  };

  const onReset = () => {
    dispatch(
      setActiveUsersFilters({
        ...activeUsers,
        searchText: '',
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setSearchText('');
  };

  const applyFilterHandler = (filterValues) => {
    dispatch(
      setActiveUsersFilters({
        ...activeUsers,
        ...filterValues,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setFilterParams(filterValues);
  };

  const handleSort = (e) => {
    dispatch(
      setActiveUsersFilters({
        ...activeUsers,
        sort: e.target.value,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setSort(e.target.value);
  };

  const resetFilterParams = () => {
    setFilterParams(FILTER_DATA.USER.INITIAL_PARAMS);
    dispatch(resetActiveUsersFiltersState());
  };

  return (
    <>
      <Box className={classes.searchFilterWrap}>
        <Box className={classes.searchWrap}>
          <SearchBar searchText={searchText} onSearch={searchHandler} onReset={onReset} />
        </Box>
        <Box px={3}>
          <NewFilterDrawer
            name={TITLE.SORT_BY.toUpperCase()}
            title={TITLE.SORT_BY}
            drawerType={DRAWER_TYPES.SORT_DRAWER}
            startIcon={<>{sortIcon}</>}
            onSortSelect={handleSort}
            selectedSort={sort}
            sortData={SORT_CONSTANT.USER_SORT}
          />
        </Box>
        <Box>
          <NewFilterDrawer
            filteredValues={filterParams}
            name={TITLE.FILTERS.toUpperCase()}
            title={TITLE.FILTERS}
            drawerType={DRAWER_TYPES.FILTER_DRAWER}
            onReset={resetFilterParams}
            onApplyFilter={(v) => {
              applyFilterHandler(v);
            }}
            filterData={filterData}
            emptyFilterLength={JSON.stringify(FILTER_DATA.USER.INITIAL_PARAMS).length}
          />
        </Box>
      </Box>
      <UsersTable searchParams={searchParams} onTotalUser={onTotalUsers} activeUsers={activeUsers} tab="active" />
    </>
  );
};

export default ActiveTab;
